import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SidebarProvider } from './contexts/SidebarContext';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { BASE_PATH } from './config';
import Layout from './components/Layout/Layout';
import Dashboard from './pages/Dashboard';
import Summary from './pages/Summary';
import Templates from './pages/Templates';
import NewTemplate from './pages/NewTemplate';
import ViewTemplate from './pages/ViewTemplate';
import NewAgreementRequest from './pages/NewAgreementRequest';
import DraftingWorkspace from './pages/DraftingWorkspace';
import AgreementDetail from './pages/AgreementDetail';
import TaskDetail from './pages/TaskDetail';
import TaskDrafting from './pages/TaskDrafting';
import TaskDraftingV2 from './pages/TaskDraftingV2';
import TaskReview from './pages/TaskReview';
import AgreementBoard from './pages/AgreementBoard';
import AgreementBoardV2 from './pages/AgreementBoardV2';
import Admin from './pages/Admin';
import Login from './pages/Login';
import ForgetPassword from './pages/ForgetPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Documentation from './pages/Documentation';
import Support from './pages/Support';
import Profile from './pages/Profile';
import NotFound from './pages/NotFound';
import ServiceUnavailable from './pages/ServiceUnavailable';
import Unauthorized from './pages/Unauthorized';
import LandingPage from './pages/LandingPage';
import AdminSidebar from './components/AdminSidebar';
import TemplatesSidebar from './components/TemplatesSidebar';
import DashboardSidebar from './components/DashboardSidebar';
import ProtectedRoute from './components/ProtectedRoute';

function App() {
  const [adminActiveTab, setAdminActiveTab] = useState('access');
  const [adminActiveSubTab, setAdminActiveSubTab] = useState('users');

  return (
    <ThemeProvider>
      <AuthProvider>
        <SidebarProvider>
          <Router basename={BASE_PATH}>
            <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forget-password" element={<ForgetPassword />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/documentation" element={<Documentation />} />
                <Route path="/support" element={<Support />} />
                <Route path="/401" element={<Unauthorized />} />
                <Route path="/503" element={<ServiceUnavailable />} />

                {/* Protected Routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Layout
                      sidebarContent={<DashboardSidebar isCollapsed={false} />}
                      sidebarCollapsedContent={<DashboardSidebar isCollapsed={true} />}
                    >
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/summary" element={
                  <ProtectedRoute>
                    <Layout showSidebar={false}>
                      <Summary />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/templates/new" element={
                  <ProtectedRoute requireTemplatesEditor={true}>
                    <Layout
                      sidebarContent={<TemplatesSidebar isCollapsed={false} />}
                      sidebarCollapsedContent={<TemplatesSidebar isCollapsed={true} />}
                    >
                      <NewTemplate />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/templates/view/:id" element={
                  <ProtectedRoute requireTemplatesScope={true}>
                    <ViewTemplate />
                  </ProtectedRoute>
                } />
                <Route path="/templates" element={
                  <ProtectedRoute requireTemplatesScope={true}>
                    <Templates />
                  </ProtectedRoute>
                } />
                <Route path="/new-request" element={
                  <ProtectedRoute>
                    <NewAgreementRequest />
                  </ProtectedRoute>
                } />
                <Route path="/drafting/:id" element={
                  <ProtectedRoute>
                    <DraftingWorkspace />
                  </ProtectedRoute>
                } />
                <Route path="/agreement/:id" element={
                  <ProtectedRoute>
                    <TaskDetail />
                  </ProtectedRoute>
                } />
                <Route path="/agreement/:id/draft" element={
                  <ProtectedRoute>
                    <TaskDrafting />
                  </ProtectedRoute>
                } />
                <Route path="/agreement/:id/draft-v2" element={
                  <ProtectedRoute>
                    <TaskDraftingV2 />
                  </ProtectedRoute>
                } />
                <Route path="/agreement/:id/review" element={
                  <ProtectedRoute>
                    <TaskReview />
                  </ProtectedRoute>
                } />
                <Route path="/board" element={
                  <ProtectedRoute>
                    <Layout showSidebar={false} mainContentClass="flex-1" footerClass="" showFooter={false}>
                      <AgreementBoardV2 />
                    </Layout>
                  </ProtectedRoute>
                } />
                {/* Redirect old v2 route to main board */}
                <Route path="/board-v2" element={<Navigate to="/board" replace />} />
                <Route path="/admin" element={
                  <ProtectedRoute requireAdmin={true}>
                    <Layout
                      sidebarContent={
                        <AdminSidebar
                          activeTab={adminActiveTab}
                          onTabChange={(tab) => {
                            setAdminActiveTab(tab);
                            // Reset sub tab when switching main tabs
                            if (tab === 'access') {
                              setAdminActiveSubTab('users');
                            } else if (tab === 'content') {
                              setAdminActiveSubTab('status');
                            }
                          }}
                          activeSubTab={adminActiveSubTab}
                          onSubTabChange={(subTab) => setAdminActiveSubTab(subTab)}
                        />
                      }
                      sidebarCollapsedContent={
                        <AdminSidebar
                          activeTab={adminActiveTab}
                          onTabChange={(tab) => {
                            setAdminActiveTab(tab);
                            if (tab === 'access') {
                              setAdminActiveSubTab('users');
                            } else if (tab === 'content') {
                              setAdminActiveSubTab('status');
                            }
                          }}
                          activeSubTab={adminActiveSubTab}
                          onSubTabChange={(subTab) => setAdminActiveSubTab(subTab)}
                        />
                      }
                    >
                      <Admin
                        activeTab={adminActiveTab}
                        onTabChange={(tab) => {
                          setAdminActiveTab(tab);
                          if (tab === 'access') {
                            setAdminActiveSubTab('users');
                          } else if (tab === 'content') {
                            setAdminActiveSubTab('status');
                          }
                        }}
                        activeSubTab={adminActiveSubTab}
                        onSubTabChange={(subTab) => setAdminActiveSubTab(subTab)}
                      />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } />
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
          </Router>
        </SidebarProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
